import {
  FetchMethod,
  type UrlBuilderParams,
  useCodeerApi,
} from "../useCodeerApi"
import type { Response } from "@/types"

const buildKnowledgeBaseNodesUrl = (params: UrlBuilderParams) =>
  `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/knowledge_bases/nodes`

const buildKnowledgeBaseNodesBatchUrl = (params: UrlBuilderParams) =>
  `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/knowledge_bases/nodes/batch`

const buildTabularCapabilitiesUrl = (params: UrlBuilderParams) =>
  `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/knowledge_bases/tabular/capabilities`

const buildTabularNodeUrl =
  (suffix = "") =>
  (params: UrlBuilderParams) =>
    `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/knowledge_bases/${params.kbId}/nodes/${params.nodeId}/tabular${suffix}`

const buildTabularDetailUrl = buildTabularNodeUrl()
const buildTabularReplaceUrl = buildTabularNodeUrl("/replace")
const buildTabularRefreshUrl = buildTabularNodeUrl("/refresh")
const buildTabularRetryUrl = buildTabularNodeUrl("/retry")

export interface KnowledgeNodeSchema {
  id: string
  knowledge_base_id: string
  parent_id?: string | null
  node_type: "FOLDER" | "FILE"
  name: string
  description?: string | null
  status?: string | null // only for FILE
  processing_state?: string | null
  last_processing_error?: string | null
  source_last_checked_at?: string | null
  snapshot_object_id?: number | null
  source_type?: string | null
  origin_connection_id?: number | null
  origin_object_id?: string | null
  origin_url?: string | null
  size_bytes?: number | null
  content_type?: string | null
  relative_path?: string | null
  deleted_at?: string | null
  created_at: string
  updated_at: string
}

export interface ListNodesQuery {
  parent_id?: string
  include_deleted?: boolean
  view?: "tree" | "flat"
}

export interface BatchListNodesRequest {
  ids: string[]
  include_deleted?: boolean
  include_ancestors?: boolean
}

export interface RenameNodeRequest {
  name?: string
  description?: string | null
}

export interface CreateNodeRequest {
  parent_id?: string | null
  name: string
  description?: string | null
}

export interface GoogleDriveFileItemSchema {
  object_id: string
  name?: string | null
  mime_type?: string | null
  url?: string | null
}

export interface AddFromGoogleDriveRequest {
  parent_id: string
  connection_id: number
  files: GoogleDriveFileItemSchema[]
}

export interface AddFromGoogleDriveResultItemSchema {
  id: string
  name: string
  status: string
}

export interface AddFromGoogleDriveResponseSchema {
  nodes: AddFromGoogleDriveResultItemSchema[]
}

export interface AddFromNotionRequest {
  parent_id: string
  connection_id: number
}

export interface AddFromNotionResultItemSchema {
  id: string
  name: string
  status: string
}

export interface AddFromNotionResponseSchema {
  nodes: AddFromNotionResultItemSchema[]
}

export interface UploadFilesResponseItemSchema {
  node_id?: string | null
  status: string
  original_name?: string | null
  content_type?: string | null
  size_bytes?: number | null
  error_message?: string | null
}

export interface UploadFilesResponseSchema {
  nodes: UploadFilesResponseItemSchema[]
}

export interface FileStatusItemSchema {
  node_id: string
  status: string
  processing_state?: string | null
  updated_at: string
  error_message?: string | null
  last_processing_error?: string | null
}

export interface TabularCapabilitiesSchema {
  enabled: boolean
  accepted_extensions: string[]
  accepted_mime_types: string[]
  max_source_bytes: number
}

export interface TabularWarningSchema {
  code: string
  message: string
  recovery_hint?: string | null
  metadata: Record<string, string | number | boolean>
}

export interface TabularColumnSchema {
  id: string
  name: string
  type: string
  nullable: boolean
  match_profile: string[]
  decimal_scale?: number | null
}

export interface TabularTableSchema {
  id: string
  name: string
  row_count: number
  columns: TabularColumnSchema[]
  warnings: TabularWarningSchema[]
}

export interface TabularDetailSchema {
  node_id: string
  feature_enabled: boolean
  available: boolean
  status: string
  processing_state: string
  last_processing_error?: string | null
  source_type?: string | null
  source_last_checked_at?: string | null
  tables: TabularTableSchema[]
  warnings: TabularWarningSchema[]
}

export interface TabularRefreshResponseSchema {
  node: KnowledgeNodeSchema
  refresh_started: boolean
}

export interface WorkspaceFileStatusRequestSchema {
  node_ids: string[]
}

export interface FileContentResponseSchema {
  node_id: string
  status: string
  name: string
  content?: string | null
}

export interface ContextObjFAQTextRangeSchema {
  start_line: number
  start_column: number
  end_line: number
  end_column: number
  start_offset?: number | null
  end_offset?: number | null
  text?: string | null
}

export interface KnowledgeBaseContextObjFAQSchema {
  id: number
  node_id: string
  context_object_id: number
  question: string
  ranges: ContextObjFAQTextRangeSchema[]
  has_question_embedding: boolean
  created_at: string
  updated_at: string
}

export interface CreateKnowledgeBaseContextObjFAQRequest {
  question: string
  ranges?: ContextObjFAQTextRangeSchema[]
}

export interface UpdateKnowledgeBaseContextObjFAQRequest {
  question?: string
  ranges?: ContextObjFAQTextRangeSchema[]
}

/**
 * Node-based Knowledge Base APIs
 *
 * We model knowledge bases as root `KnowledgeNode`s (parent_id is NULL).
 * This unified surface supports:
 * - Listing KB roots (parent_id omitted) and listing folder children (parent_id provided)
 * - Creating KB roots (parent_id omitted) and folders (parent_id provided)
 * - Updating/deleting nodes by id (works for KB roots and children)
 */
export const useListKnowledgeBaseNodesApi = () => {
  return useCodeerApi<Response<KnowledgeNodeSchema[]>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/nodes",
    FetchMethod.Get,
    true,
    buildKnowledgeBaseNodesUrl
  )
}

export const useBatchKnowledgeBaseNodesApi = () => {
  return useCodeerApi<Response<KnowledgeNodeSchema[]>, BatchListNodesRequest>(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/nodes/batch",
    FetchMethod.Post,
    true,
    buildKnowledgeBaseNodesBatchUrl
  )
}

export const useCreateKnowledgeBaseNodeApi = () => {
  return useCodeerApi<Response<KnowledgeNodeSchema>, CreateNodeRequest>(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/nodes",
    FetchMethod.Post,
    true,
    (params) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/knowledge_bases/nodes`
  )
}

export const useUpdateKnowledgeBaseNodeApi = () => {
  return useCodeerApi<Response<KnowledgeNodeSchema>, RenameNodeRequest>(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/nodes/__placeholder__",
    FetchMethod.Patch,
    true,
    (params) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/knowledge_bases/nodes/${params.nodeId}`
  )
}

export const useDeleteKnowledgeBaseNodeApi = () => {
  return useCodeerApi<Response<unknown>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/nodes/__placeholder__",
    FetchMethod.Delete,
    true,
    (params) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/knowledge_bases/nodes/${params.nodeId}`
  )
}

export const useAddKnowledgeBaseFilesFromGoogleDriveByKbIdApi = () => {
  return useCodeerApi<
    Response<AddFromGoogleDriveResponseSchema>,
    AddFromGoogleDriveRequest
  >(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/__placeholder__/files/import/google_drive",
    FetchMethod.Post,
    true,
    (params) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/knowledge_bases/${params.kbId}/files/import/google_drive`
  )
}

export const useAddKnowledgeBaseFilesFromNotionByKbIdApi = () => {
  return useCodeerApi<
    Response<AddFromNotionResponseSchema>,
    AddFromNotionRequest
  >(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/__placeholder__/files/import/notion",
    FetchMethod.Post,
    true,
    (params) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/knowledge_bases/${params.kbId}/files/import/notion`
  )
}

export const useUploadKnowledgeBaseFilesByKbIdApi = () => {
  return useCodeerApi<Response<UploadFilesResponseSchema>, FormData>(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/__placeholder__/files/upload",
    FetchMethod.Post,
    true,
    (params) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/knowledge_bases/${params.kbId}/files/upload`
  )
}

export const useKnowledgeBaseFileStatusByWorkspaceApi = () => {
  return useCodeerApi<
    Response<FileStatusItemSchema[]>,
    WorkspaceFileStatusRequestSchema
  >(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/files/status",
    FetchMethod.Post,
    true,
    (params) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/knowledge_bases/files/status`
  )
}

export const useKnowledgeBaseFileContentByKbIdApi = () => {
  return useCodeerApi<Response<FileContentResponseSchema>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/__placeholder__/nodes/__placeholder__/content",
    FetchMethod.Get,
    true,
    (params) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/knowledge_bases/${params.kbId}/nodes/${params.nodeId}/content`
  )
}

export const useKnowledgeBaseTabularCapabilitiesApi = () => {
  return useCodeerApi<Response<TabularCapabilitiesSchema>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/tabular/capabilities",
    FetchMethod.Get,
    true,
    buildTabularCapabilitiesUrl
  )
}

export const useKnowledgeBaseTabularDetailApi = () => {
  return useCodeerApi<Response<TabularDetailSchema>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/__placeholder__/nodes/__placeholder__/tabular",
    FetchMethod.Get,
    true,
    buildTabularDetailUrl
  )
}

export const useReplaceKnowledgeBaseTabularSourceApi = () => {
  return useCodeerApi<Response<KnowledgeNodeSchema>, FormData>(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/__placeholder__/nodes/__placeholder__/tabular/replace",
    FetchMethod.Post,
    true,
    buildTabularReplaceUrl
  )
}

export const useRefreshKnowledgeBaseTabularSourceApi = () => {
  return useCodeerApi<Response<TabularRefreshResponseSchema>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/__placeholder__/nodes/__placeholder__/tabular/refresh",
    FetchMethod.Post,
    true,
    buildTabularRefreshUrl
  )
}

export const useRetryKnowledgeBaseTabularSourceApi = () => {
  return useCodeerApi<Response<KnowledgeNodeSchema>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/__placeholder__/nodes/__placeholder__/tabular/retry",
    FetchMethod.Post,
    true,
    buildTabularRetryUrl
  )
}

export const useListKnowledgeBaseNodeFaqsApi = () => {
  return useCodeerApi<Response<KnowledgeBaseContextObjFAQSchema[]>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/__placeholder__/nodes/__placeholder__/faqs",
    FetchMethod.Get,
    true,
    (params) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/knowledge_bases/${params.kbId}/nodes/${params.nodeId}/faqs`
  )
}

export const useCreateKnowledgeBaseNodeFaqApi = () => {
  return useCodeerApi<
    Response<KnowledgeBaseContextObjFAQSchema>,
    CreateKnowledgeBaseContextObjFAQRequest
  >(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/__placeholder__/nodes/__placeholder__/faqs",
    FetchMethod.Post,
    true,
    (params) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/knowledge_bases/${params.kbId}/nodes/${params.nodeId}/faqs`
  )
}

export const useUpdateKnowledgeBaseNodeFaqApi = () => {
  return useCodeerApi<
    Response<KnowledgeBaseContextObjFAQSchema>,
    UpdateKnowledgeBaseContextObjFAQRequest
  >(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/__placeholder__/nodes/__placeholder__/faqs/__placeholder__",
    FetchMethod.Patch,
    true,
    (params) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/knowledge_bases/${params.kbId}/nodes/${params.nodeId}/faqs/${params.faqId}`
  )
}

export const useDeleteKnowledgeBaseNodeFaqApi = () => {
  return useCodeerApi<Response<unknown>>(
    "/organizations/__placeholder__/workspaces/__placeholder__/knowledge_bases/__placeholder__/nodes/__placeholder__/faqs/__placeholder__",
    FetchMethod.Delete,
    true,
    (params) =>
      `/organizations/${params.organizationId}/workspaces/${params.workspaceId}/knowledge_bases/${params.kbId}/nodes/${params.nodeId}/faqs/${params.faqId}`
  )
}
